
import { useState } from 'react';

const SECTOR_OPTIONS = [
  'Industria Manufacturera',
  'Sector Salud',
  'Sector Gubernamental',
  'Hotelería y Turismo',
  'Industria Alimenticia',
  'Oficinas Corporativas',
  'Restaurantes y Cafeterías',
  'Comercios y Retail',
  'Educación',
  'Otro',
];

const VOLUMEN_OPTIONS = [
  'Menos de $5,000 MXN/mes',
  '$5,000 – $15,000 MXN/mes',
  '$15,000 – $50,000 MXN/mes',
  'Más de $50,000 MXN/mes',
  'Por definir / Cotización inicial',
];

export default function Contact() {
  const [formData, setFormData] = useState({
    nombre: '',
    email: '',
    telefono: '',
    empresa: '',
    sector: '',
    volumen: '',
    mensaje: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // simple client‑side validation
    if (formData.mensaje.length > 500) {
      setSubmitStatus('error');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => formBody.append(key, value));

      const response = await fetch('https://readdy.ai/api/form/d6fko6qau1b90a4042e0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formBody.toString(),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          nombre: '',
          email: '',
          telefono: '',
          empresa: '',
          sector: '',
          volumen: '',
          mensaje: '',
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (err) {
      console.error('Form submission error:', err);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      {/* Floating WhatsApp Button */}
      <a
        href="https://wa.me/524492037021?text=Hola%2C%20me%20interesa%20solicitar%20una%20cotizaci%C3%B3n%20empresarial"
        className="fixed bottom-6 right-6 z-50 w-14 h-14 bg-green-600 rounded-full flex items-center justify-center shadow-lg hover:bg-green-700 transition-all hover:scale-110 cursor-pointer"
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Contactar por WhatsApp"
      >
        <div className="w-14 h-14 flex items-center justify-center">
          <i className="ri-whatsapp-line text-2xl"></i>
        </div>
      </a>

      <section id="contacto" className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="text-center mb-16">
            <span className="inline-block text-xs font-semibold tracking-widest text-blue-700 uppercase mb-4 bg-blue-50 px-4 py-1.5 rounded-full">
              Contacto Empresarial
            </span>
            <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-5 tracking-tight">
              Solicita tu Cotización<br />
              <span className="text-blue-700">Empresarial</span>
            </h2>
            <p className="text-gray-500 text-lg max-w-2xl mx-auto leading-relaxed">
              Cuéntanos sobre tu empresa y te preparamos una propuesta personalizada de suministro industrial.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-5 gap-10">
            {/* Left: Info + Map */}
            <div className="lg:col-span-2 flex flex-col gap-6">
              {/* Contact Info Card */}
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8">
                <h3 className="text-lg font-bold text-gray-900 mb-6">Información de Contacto</h3>
                <div className="space-y-5">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-blue-50 rounded-xl flex items-center justify-center flex-shrink-0">
                      <i className="ri-map-pin-2-line text-blue-700 text-lg"></i>
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1">Dirección</p>
                      <p className="text-gray-700 text-sm leading-relaxed">
                        Camino San Bartolo 240<br />
                        Aguascalientes, México<br />
                        C.P. 20270
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-blue-50 rounded-xl flex items-center justify-center flex-shrink-0">
                      <i className="ri-phone-line text-blue-700 text-lg"></i>
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1">Teléfono</p>
                      <a href="tel:4499171543" className="text-blue-700 hover:text-blue-800 text-sm font-semibold cursor-pointer">
                        449 917 1543
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-blue-50 rounded-xl flex items-center justify-center flex-shrink-0">
                      <i className="ri-time-line text-blue-700 text-lg"></i>
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1">Horario</p>
                      <p className="text-gray-700 text-sm leading-relaxed">
                        Lunes a Viernes: 9:00 AM – 5:00 PM<br />
                        Sábado: 9:00 AM – 2:00 PM<br />
                        <span className="text-green-600 font-semibold">● Abierto ahora</span>
                      </p>
                    </div>
                  </div>
                </div>

                <div className="mt-7 pt-7 border-t border-gray-100 flex flex-col sm:flex-row gap-3">
                  <a
                    href="tel:4499171543"
                    className="flex-1 flex items-center justify-center gap-2 px-5 py-3 bg-blue-700 text-white text-sm font-semibold rounded-xl hover:bg-blue-800 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    <div className="w-5 h-5 flex items-center justify-center">
                      <i className="ri-phone-line text-base"></i>
                    </div>
                    Llamar ahora
                  </a>
                  <a
                    href="https://wa.me/524492037021?text=Hola%2C%20me%20interesa%20solicitar%20una%20cotizaci%C3%B3n"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 flex items-center justify-center gap-2 px-5 py-3 bg-green-500 text-white text-sm font-semibold rounded-xl hover:bg-green-600 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    <div className="w-5 h-5 flex items-center justify-center">
                      <i className="ri-whatsapp-line text-base"></i>
                    </div>
                    WhatsApp
                  </a>
                </div>
              </div>

              {/* Map */}
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden flex-1 min-h-64">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3702.5!2d-102.3162!3d21.8753!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x8429ef0b692d3ced%3A0x400!2sCamino+San+Bartolo+240%2C+Aguascalientes%2C+M%C3%A9xico!5e0!3m2!1ses!2smx!4v1700000000000!5m2!1ses!2smx"
                  width="100%"
                  height="100%"
                  style={{ border: 0, minHeight: '260px' }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Ubicación HIPERCLEAN DE AGUASCALIENTES"
                ></iframe>
              </div>
            </div>

            {/* Right: Form */}
            <div className="lg:col-span-3">
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 h-full">
                <div className="flex items-center gap-3 mb-7">
                  <div className="w-10 h-10 bg-blue-700 rounded-xl flex items-center justify-center">
                    <i className="ri-file-list-3-line text-white text-lg"></i>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900">Formulario de Cotización</h3>
                    <p className="text-xs text-gray-400">Respuesta en menos de 24 horas hábiles</p>
                  </div>
                </div>

                <form id="cotizacion-empresarial-form" onSubmit={handleSubmit} data-readdy-form>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label htmlFor="nombre" className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                        Nombre Completo *
                      </label>
                      <input
                        type="text"
                        id="nombre"
                        name="nombre"
                        value={formData.nombre}
                        onChange={handleChange}
                        required
                        placeholder="Tu nombre"
                        className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm bg-gray-50 focus:bg-white transition-colors"
                      />
                    </div>

                    <div>
                      <label htmlFor="email" className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                        Correo Electrónico *
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        placeholder="correo@empresa.com"
                        className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm bg-gray-50 focus:bg-white transition-colors"
                      />
                    </div>

                    <div>
                      <label htmlFor="telefono" className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                        Teléfono *
                      </label>
                      <input
                        type="tel"
                        id="telefono"
                        name="telefono"
                        value={formData.telefono}
                        onChange={handleChange}
                        required
                        placeholder="449 123 4567"
                        className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm bg-gray-50 focus:bg-white transition-colors"
                      />
                    </div>

                    <div>
                      <label htmlFor="empresa" className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                        Nombre de la Empresa *
                      </label>
                      <input
                        type="text"
                        id="empresa"
                        name="empresa"
                        value={formData.empresa}
                        onChange={handleChange}
                        required
                        placeholder="Razón social o nombre comercial"
                        className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm bg-gray-50 focus:bg-white transition-colors"
                      />
                    </div>

                    <div>
                      <label htmlFor="sector" className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                        Sector / Giro Empresarial *
                      </label>
                      <select
                        id="sector"
                        name="sector"
                        value={formData.sector}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm bg-gray-50 focus:bg-white transition-colors cursor-pointer"
                      >
                        <option value="">Selecciona tu sector</option>
                        {SECTOR_OPTIONS.map((s) => (
                          <option key={s} value={s}>{s}</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label htmlFor="volumen" className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                        Volumen Estimado de Compra *
                      </label>
                      <select
                        id="volumen"
                        name="volumen"
                        value={formData.volumen}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm bg-gray-50 focus:bg-white transition-colors cursor-pointer"
                      >
                        <option value="">Selecciona un rango</option>
                        {VOLUMEN_OPTIONS.map((v) => (
                          <option key={v} value={v}>{v}</option>
                        ))}
                      </select>
                    </div>

                    <div className="sm:col-span-2">
                      <label htmlFor="mensaje" className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                        Descripción de Necesidades *
                      </label>
                      <textarea
                        id="mensaje"
                        name="mensaje"
                        value={formData.mensaje}
                        onChange={handleChange}
                        required
                        maxLength={500}
                        rows={4}
                        placeholder="Describe los productos que necesitas, frecuencia de pedido, condiciones especiales..."
                        className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm bg-gray-50 focus:bg-white transition-colors resize-none"
                      ></textarea>
                      <div className="flex justify-between mt-1">
                        <span className="text-xs text-gray-400">Máximo 500 caracteres</span>
                        <span className={`text-xs font-medium ${formData.mensaje.length > 480 ? 'text-red-500' : 'text-gray-400'}`}>
                          {formData.mensaje.length}/500
                        </span>
                      </div>
                    </div>

                    <div className="sm:col-span-2">
                      <button
                        type="submit"
                        disabled={isSubmitting || formData.mensaje.length > 500}
                        className="w-full flex items-center justify-center gap-2 px-6 py-4 bg-blue-700 text-white font-semibold rounded-xl hover:bg-blue-800 transition-all duration-200 disabled:bg-gray-300 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer shadow-md hover:shadow-lg"
                      >
                        {isSubmitting ? (
                          <>
                            <div className="w-5 h-5 flex items-center justify-center">
                              <i className="ri-loader-4-line text-lg animate-spin"></i>
                            </div>
                            Enviando solicitud...
                          </>
                        ) : (
                          <>
                            <div className="w-5 h-5 flex items-center justify-center">
                              <i className="ri-send-plane-2-line text-lg"></i>
                            </div>
                            Solicitar Cotización Empresarial
                          </>
                        )}
                      </button>
                    </div>

                    {submitStatus === 'success' && (
                      <div className="sm:col-span-2 bg-green-50 border border-green-200 rounded-xl p-4 flex items-start gap-3">
                        <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
                          <i className="ri-checkbox-circle-fill text-green-600 text-xl"></i>
                        </div>
                        <div>
                          <h4 className="font-bold text-green-900 text-sm">¡Solicitud enviada con éxito!</h4>
                          <p className="text-green-700 text-xs mt-1">
                            Nuestro equipo comercial revisará tu solicitud y te contactará en menos de 24 horas hábiles.
                          </p>
                        </div>
                      </div>
                    )}

                    {submitStatus === 'error' && (
                      <div className="sm:col-span-2 bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-3">
                        <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
                          <i className="ri-error-warning-fill text-red-600 text-xl"></i>
                        </div>
                        <div>
                          <h4 className="font-bold text-red-900 text-sm">Error al enviar</h4>
                          <p className="text-red-700 text-xs mt-1">
                            Por favor intenta nuevamente o contáctanos directamente al 449 917 1543.
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
